import {
	ChangeDetectionStrategy,
	Component,
	inject,
	input,
	OnInit,
	output,
	signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';

import { IUser } from '@webapp/users/interfaces/users.interface';
import { UsersService } from '@webapp/users/services/users.service';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-search-users',
	standalone: true,
	imports: [ReactiveFormsModule, MatAutocompleteModule, MatOption],
	templateUrl: './search-users.component.html',
	styleUrls: ['./search-users.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchUsersComponent implements OnInit {
	private readonly usersService = inject(UsersService);
	searchControl = new FormControl('')
	users = signal<IUser[]>([]);
	loading = signal(false);
	userSelected = output<IUser>();
	label = input('');
	userTypes = input<string | string[]>('');
	userRoles = input<string | string[]>([]);
	agentId = input<string>('');
	private destroy$ = new Subject<void>();

	displayUser = (user?: IUser) => {
		const company = user?.companyName?.trim();
		if (company) {
			return company;
		}

		const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
		return fullName;
	};

	ngOnInit() {
		this.searchControl.valueChanges
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.destroy$),
				filter(value => typeof value === 'string')

			)
			.subscribe((value) => {
				this.getUsers(value);
			});
	}

	getName(user: IUser) {
		return user.companyName ? user.companyName : `${user.firstName} ${user.lastName}`;
	}
	setUser(event: MatAutocompleteSelectedEvent) {
		const user: IUser = event.option.value
		this.userSelected.emit(user);
	}

	private async getUsers(searchString: string) {
		if (searchString) {
			this.loading.set(true);
			try {
				const filters: Record<string, string | Record<string, string | undefined> | undefined> = {};
				const userTypesInput = this.userTypes();
				const userRolesInput = this.userRoles();
				const agentIdInput = this.agentId();
				
				// Handle userTypes (person/business distinction) - only single value
				if (userTypesInput) {
					if (Array.isArray(userTypesInput)) {
						if (userTypesInput.length > 0) {
							filters['type'] = userTypesInput[0]; // Only take the first one
						}
					} else if (userTypesInput.trim()) {
						filters['type'] = userTypesInput;
					}
				}

				// Handle userRoles (CLIENT, PROSPECT, AGENT, etc)
				if (userRolesInput) {
					if (Array.isArray(userRolesInput)) {
						if (userRolesInput.length > 0) {
							filters['role'] = userRolesInput[0]; // Take only the first role
						}
					} else if (userRolesInput.trim()) {
						filters['role'] = userRolesInput;
					}
				}

				// Handle agentId
				if (agentIdInput && agentIdInput.trim()) {
					filters['agentId'] = agentIdInput;
				}

				const users = await firstValueFrom(
					this.usersService.get({
						search: searchString,
						filters,
					})
				);

				const usersData = users.data as unknown as IUser[];
				this.users.set(usersData);
			} catch (error) {
				throw new Error(`Error al obtener los usuarios: ${error}`);
			} finally {
				this.loading.set(false);
			}
		} else {
			this.users.set([]);
			this.loading.set(false);
		}
	}
}
